/* eslint-disable no-console */
import { parentsThrough, hasData, copyElement } from "./dom-helpers.js";
const HEADING_SELECTOR = `[data-heading]`;
const HEADINGS_INCREMENT = `[data-headings-increment]`;
const HEADINGS_START = `[data-headings-start]`;

const IDS_TO_IGNORE_IN_CRAWL = ["fb-root"];

function getHeadingType(map, node) {
  const level = map[node.id].level;
  if (typeof level === "number") {
    return `h${level}`;
  }
  return console.error(`[getHeadingType] Something went wrong -- level "${level}" is not a number`);
}

function traverseOnSelector(nodes, matchingSelector, cb, depth = 0) {
  const result = [];
  nodes.forEach((node, idx) => {
    if (!IDS_TO_IGNORE_IN_CRAWL.includes(node.id)) {
      const within = node.querySelectorAll(matchingSelector);
      const children = traverseOnSelector(within, matchingSelector, cb, depth + 1);
      result[idx] = cb ? cb({ children, node, depth }) : { children, node, depth };
    }
  });
  return result;
}

function transformRecursive(array, callback) {
  const newArray = [];
  // eslint-disable-next-line
  for (let a in array) {
    let item = array[a];
    const newItem = callback(item);
    newArray.push({
      ...newItem,
      children: transformRecursive(newItem.children, callback)
    });
  }
  return newArray;
}

function getHeadings(node) {
  const allHeadings = node.querySelectorAll(HEADING_SELECTOR);
  /* filter out headings that belong to an incrementing node that is NOT the current one */
  const headings = [...allHeadings].filter((h) => {
    // get headings that alreadyBelong to a node that increments
    const parents = parentsThrough(h, (n) => hasData(n, "headingsIncrement"));
    const incrementingNode = parents[parents.length - 1];
    return incrementingNode.isSameNode(node);
  });

  return headings;
}

function getHeadingToIncrement(node) {
  const parents = parentsThrough(node, (n) => hasData(n, "headingsIncrement"));
  const incrementingNode = parents[parents.length - 1];
  return incrementingNode.querySelector(HEADING_SELECTOR);
}

function decorateWithHeadings(headingCallback) {
  return (obj) => {
    const headings = getHeadings(obj.node);
    const headingToIncrement = getHeadingToIncrement(obj.node);

    if (headingCallback && typeof headingCallback === "function") {
      headingCallback({ ...obj, headings, headingToIncrement });
    }
    return { ...obj, headings, headingToIncrement };
  };
}

function incrementHeadingLevel(data, store) {
  const { headingToIncrement, depth } = data;
  let levelToIncrement = 0;
  if (headingToIncrement && headingToIncrement?.id) {
    const mapEntry = store[headingToIncrement.id];
    if (mapEntry.level) {
      levelToIncrement = mapEntry.level;
    }
  } else {
    const h1AlreadyExists = Object.values(store)
      .map((v) => v.level)
      .includes(1);
    // increment heading if there is already an h1 on the page, no matter the container structure
    if (levelToIncrement === 0 && h1AlreadyExists) {
      levelToIncrement = 1;
    } else if (levelToIncrement === 6) {
      // return the level without incrementing if we are already at 6
      return levelToIncrement;
    } else {
      levelToIncrement = depth;
    }
  }

  return levelToIncrement + 1;
}

function createHeadingMap(rootNode, allHeadings) {
  /*
   *  1) Store Setup
   *  2) ID Validation
   *  3) Heading Level Assignment & Storage
   */

  /* -------- 1) Store Setup -------- */
  let HEADINGS_MAP_STORE = {};

  allHeadings.forEach((e, idx) => {
    HEADINGS_MAP_STORE[e.id] = { index: idx, id: e.id };
  });

  const saveToStore = (data) => {
    const { headings } = data;
    headings.forEach((h) => {
      const existing = HEADINGS_MAP_STORE[h.id];
      if (!existing) {
        console.error("Headings logic Malfunction. This shouldn't happen.");
      }
      let level = incrementHeadingLevel(data, HEADINGS_MAP_STORE);
      HEADINGS_MAP_STORE[h.id] = { ...existing, level };
    });
  };

  /* -------- 2) ID Validation  -------- */
  const headingIds = Object.keys(HEADINGS_MAP_STORE);
  const allUnique = new Set(headingIds).size === headingIds.length;
  console.assert(
    allUnique,
    "[Headings] Duplicate heading IDs detected. This is bad for accessibility, and it may cause unexepectd behavior in heading assignment logic. "
  );

  /* -------- 3) Heading Assignment  -------- */

  // starting from the rootNode, get increment containers
  const startingContainer = rootNode.querySelector(HEADINGS_START);
  if (!startingContainer) {
    throw Error(`Heading Assignment Failed. No element with ${HEADINGS_START} found.`);
  }
  const topLevelContainers = startingContainer.querySelectorAll(
    `${HEADINGS_INCREMENT}:not(${HEADINGS_INCREMENT} ${HEADINGS_INCREMENT})`
  );
  const containerTree = traverseOnSelector(topLevelContainers, HEADINGS_INCREMENT);

  // Do the assignment. While we are there, store the data after each heading decoration
  // TODO: rewrite this to use promises.
  transformRecursive(containerTree, decorateWithHeadings(saveToStore));
  return HEADINGS_MAP_STORE;
}

function assignLevels(nodes, map, rootNode) {
  console.assert(nodes.length > 0, "[assignLevels] No nodes provided to function");
  return new Promise((resolve, reject) => {
    try {
      nodes.forEach((node, idx) => {
        const elementType = getHeadingType(map, node);
        const element = copyElement({
          element: node,
          elementType,
          /*
          Dev Experience Todo: make this run by default in DEV mode
          >> uncomment the next line for heading debugging!
           */
          // textContent: `${elementType} ${node.textContent}`
          textContent: node.textContent,
          id: node.id,
          rootNode
        });

        node.parentElement.replaceChild(element, node);
        if (idx === nodes.length - 1) {
          resolve(rootNode);
        }
      });
    } catch (e) {
      reject(e);
    }
  });
}

export async function transformDocumentHeadings(document) {
  const headings = [...document.querySelectorAll(HEADING_SELECTOR)];
  const headingMap = createHeadingMap(document, headings);
  await assignLevels(headings, headingMap, document);

  return document;
}
